import React, { useState } from 'react';
import { reimpressaoCupomService } from '../../services/api';
import DraggableWindow from './DraggableWindow';

const fmt = (v) => (Number(v) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatChave = (ch) => String(ch || '').replace(/\D/g, '').replace(/(\d{4})(?=\d)/g, '$1 ').trim();
const formatCnpj = (value) => {
  const digits = String(value || '').replace(/\D/g, '').padStart(14, '0');
  if (digits.length !== 14) return String(value || '');
  return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
};
const formatCep = (value) => {
  const digits = String(value || '').replace(/\D/g, '').padStart(8, '0');
  if (digits.length !== 8) return String(value || '');
  return digits.replace(/^(\d{5})(\d{3})$/, '$1-$2');
};
const formatDateBr = (value) => {
  const str = String(value || '').slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [y, m, d] = str.split('-');
    return `${d}/${m}/${y}`;
  }
  return str || '-';
};
const FORMA_PAGAMENTO_LABELS = {
  1: 'Dinheiro',
  4: 'Cartao',
  5: 'Cartao',
  8: 'PIX',
  9: 'Convenio',
  10: 'Vale',
  11: 'Vale'
};

// pega o 1º campo existente entre várias chaves possíveis (nomes variam)
const pick = (obj, keys, def = '') => {
  for (const k of keys) if (obj && obj[k] != null && obj[k] !== '') return obj[k];
  return def;
};

const formatarPagamento = (item) => {
  if (!item) return '-';
  const forma = parseInt(item.FORMAPG, 10);
  const partes = [FORMA_PAGAMENTO_LABELS[forma] || `Forma ${item.FORMAPG || '-'}`];
  const bandeira = String(item.BANDEIRA || '').trim();
  const parcelas = parseInt(item.PARCELAS, 10);
  if (bandeira) partes.push(bandeira);
  if (Number.isFinite(parcelas) && parcelas > 1) partes.push(`${parcelas}x`);
  return `${partes.join(' - ')} ${fmt(item.VALOR)}`;
};

const WindowReimprimirCupom = ({ onClose, onMinimize, isMinimized, zIndex, onFocus, userData }) => {
  const [cupom, setCupom] = useState('');
  const [filial, setFilial] = useState(userData?.loja_id || '');
  const [data, setData] = useState('');           // YYYY-MM-DD
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');

  const buscar = async () => {
    setErro(''); setDados(null);
    if (!String(data).trim() || !String(filial).trim() || !String(cupom).trim()) {
      setErro('Informe data, filial e número do cupom (os três são obrigatórios).');
      return;
    }
    setCarregando(true);
    try {
      const r = await reimpressaoCupomService.buscarCupom(cupom, filial, data);
      setDados(r);
    } catch (e) {
      setErro(e.message || 'Erro ao buscar o cupom.');
    } finally {
      setCarregando(false);
    }
  };

  // monta os itens normalizados
  const itens = (dados?.itens || []).map((it) => ({
    cod: pick(it, ['PRODUTO', 'CODIGO', 'CDPRODU']),
    nome: pick(it, ['NOME', 'DESCRICAO']),
    qtd: Number(pick(it, ['QTD', 'QUANTIDADE'], 0)),
    vlruni: Number(pick(it, ['VLRUNI', 'VALOR', 'PRECO'], 0)),
    desc: Number(pick(it, ['DESCONTO', 'VLDESC'], 0)),
  }));
  const cab = dados?.cabecalho || {};
  const nota = dados?.nota || null;
  const filialDados = dados?.filialDados || {};
  const pagamentos = dados?.pagamentos || [];
  const pagamentoLabel = pagamentos.length ? pagamentos.map(formatarPagamento).join(' | ') : '-';
  const dataCupom = formatDateBr(pick(cab, ['DATA']));
  const horaCupom = pick(cab, ['HORA'], '');
  const nomeCliente = String(dados?.cliente?.NOME || '').trim();
  const nomeFilial = String(filialDados.NOME || pick(cab, ['NOMEEMP', 'EMPRESA'], 'CALLFARMA')).trim();
  const ruaFilial = String(filialDados.ENDER || '').trim();
  const numeroFilial = String(filialDados.NREND || '').trim();
  const cidadeFilial = String(filialDados.CIDADE || '').trim();
  const enderecoFilial = [
    [ruaFilial, numeroFilial].filter(Boolean).join(', '),
    cidadeFilial
  ].filter(Boolean).join(' - ');
  const cepFilial = formatCep(filialDados.CEP);
  const cnpjFilial = formatCnpj(filialDados.CGC);
  const qrLink = String(nota?.LINK || '').trim();
  const protocoloNfce = String(nota?.PROTOCOLO || '').trim();
  const chaveNfce = String(nota?.CHAVE || '').replace(/\D/g, '');
  const chaveValida = chaveNfce.length >= 40;
  const nfceValida = Boolean(nota && (chaveValida || qrLink || protocoloNfce));
  const total = itens.reduce((s, i) => s + (i.qtd * i.vlruni - i.desc), 0) || Number(pick(cab, ['VLRTOTAL'], 0));

  // gera o HTML do cupom e imprime numa janela isolada (estilo bobina ~80mm)
  const imprimir = () => {
    const linhasItens = itens.map((i, n) => `
      <tr><td colspan="3" class="prod">${n + 1} ${i.cod} ${i.nome}</td></tr>
      <tr><td>${i.qtd} x ${fmt(i.vlruni)}</td><td></td><td class="r">${fmt(i.qtd * i.vlruni - i.desc)}</td></tr>`).join('');
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Cupom ${cupom}</title>
      <style>
        @page { size: 80mm auto; margin: 4mm; }
        body { font-family: 'Courier New', monospace; font-size: 11px; width: 72mm; color:#000; }
        .c{ text-align:center; } .r{ text-align:right; } .b{ font-weight:bold; }
        hr{ border:none; border-top:1px dashed #000; }
        table{ width:100%; border-collapse:collapse; } td{ vertical-align:top; }
        .prod{ padding-top:3px; } .small{ font-size:10px; } .chave{ word-break:break-all; }
        .qrbox{ text-align:center; margin:6px 0; }
        .qrbox img{ width:120px; height:120px; }
      </style></head><body>
      <div class="c b">${nomeFilial}</div>
      ${cnpjFilial ? `<div class="c small">CNPJ: ${cnpjFilial}</div>` : ''}
      ${enderecoFilial ? `<div class="c small">${enderecoFilial}</div>` : ''}
      ${cepFilial ? `<div class="c small">CEP: ${cepFilial}</div>` : ''}
      <div class="c small">Documento Auxiliar da Nota Fiscal de Consumidor Eletrônica</div>
      <hr/>
      <div class="small">Data: ${dataCupom} ${horaCupom} &nbsp; Vend: ${pick(cab, ['VENDEDOR'], '-')}</div>
      ${nomeCliente ? `<div class="small">Cliente: ${nomeCliente}</div>` : ''}
      <hr/>
      <table>${linhasItens}</table>
      <hr/>
      <table><tr><td class="b">TOTAL R$</td><td class="r b">${fmt(total)}</td></tr>
      <tr><td>Forma pgto</td><td class="r">${pagamentoLabel}</td></tr></table>
      <hr/>
      ${nfceValida ? `
        <div class="c small">Protocolo de Autorização de Uso</div>
        <div class="c small">${nota.PROTOCOLO || ''} - ${nota.DTEMIS || ''}</div>
        <div class="c small">NFC-e nº ${nota.NOTA || ''} série ${nota.SERIE || ''}</div>
        ${chaveValida ? `
          <div class="small b" style="margin-top:4px">CHAVE DE ACESSO</div>
          <div class="small chave">${formatChave(chaveNfce)}</div>
        ` : ''}
        ${qrLink
          ? `<div class="qrbox"><img alt="QR Code NFC-e" src="https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(qrLink)}" /></div><div class="small chave">${qrLink}</div>`
          : `<div class="qrbox small">QR Code indisponível</div>`}
        <div class="c small">Consulte pela Chave de Acesso em<br/>o site da SEFAZ da sua UF</div>
      ` : `<div class="c small">Cupom sem NFC-e autorizada válida vinculada.</div>`}
      <hr/>
      <div class="c small b">** 2ª VIA — NÃO É VÁLIDO COMO CUPOM FISCAL **</div>
      <script>window.onload=function(){window.print();}</script>
      </body></html>`;
    const w = window.open('', '_blank', 'width=380,height=640');
    if (!w) { setErro('Bloqueador de pop-up impediu a impressão. Libere pop-ups.'); return; }
    w.document.write(html); w.document.close();
  };

  const inp = { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' };

  return (
    <DraggableWindow title="Reimprimir Cupom (NFC-e)" icon="fa-receipt"
      onClose={onClose} onMinimize={onMinimize} isMinimized={isMinimized}
      zIndex={zIndex} onFocus={onFocus} initialWidth={460}>
      <div style={{ padding: 16 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <input style={{ ...inp, width: 140 }} type="date" value={data}
            title="Data do cupom" onChange={(e) => setData(e.target.value)} />
          <input style={{ ...inp, width: 80 }} value={filial} placeholder="Filial"
            onChange={(e) => setFilial(e.target.value)} />
          <input style={{ ...inp, flex: 1, minWidth: 120 }} value={cupom} placeholder="Nº do cupom..."
            onChange={(e) => setCupom(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') buscar(); }} autoFocus />
          <button onClick={buscar} disabled={carregando}
            style={{ padding: '8px 16px', border: 'none', borderRadius: 6, background: '#0078d7', color: '#fff', cursor: 'pointer' }}>
            {carregando ? '...' : 'Buscar'}
          </button>
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: -8, marginBottom: 12 }}>
          O nº do cupom repete por dia/loja — informe <b>data + filial + número</b> para achar o certo.
        </div>

        {dados && (
          <>
            {/* prévia do cupom */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 14, fontFamily: 'monospace', fontSize: 12, maxHeight: 340, overflowY: 'auto' }}>
              <div style={{ textAlign: 'center', fontWeight: 700 }}>{nomeFilial}</div>
              {cnpjFilial && <div style={{ textAlign: 'center', fontSize: 10, color: '#475569' }}>CNPJ: {cnpjFilial}</div>}
              {enderecoFilial && <div style={{ textAlign: 'center', fontSize: 10, color: '#475569' }}>{enderecoFilial}</div>}
              {cepFilial && <div style={{ textAlign: 'center', fontSize: 10, color: '#475569' }}>CEP: {cepFilial}</div>}
              <div style={{ textAlign: 'center', fontSize: 10, color: '#475569' }}>Documento Auxiliar da NFC-e</div>
              <hr />
              <div style={{ fontSize: 11 }}>Data: {dataCupom} {horaCupom} · Vend: {pick(cab, ['VENDEDOR'], '-')}</div>
              {nomeCliente && <div style={{ fontSize: 11 }}>Cliente: {nomeCliente}</div>}
              <hr />
              {itens.map((i, n) => (
                <div key={n} style={{ marginBottom: 3 }}>
                  <div>{n + 1} {i.cod} {i.nome}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>{i.qtd} x {fmt(i.vlruni)}</span>
                    <span>{fmt(i.qtd * i.vlruni - i.desc)}</span>
                  </div>
                </div>
              ))}
              <hr />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}><span>TOTAL R$</span><span>{fmt(total)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span>Forma pgto</span>
                <span style={{ textAlign: 'right' }}>{pagamentoLabel}</span>
              </div>
              <hr />
              {nfceValida ? (
                <div style={{ fontSize: 11 }}>
                  <div style={{ textAlign: 'center' }}>Protocolo: {nota.PROTOCOLO} - {nota.DTEMIS}</div>
                  <div style={{ textAlign: 'center' }}>NFC-e nº {nota.NOTA} série {nota.SERIE}</div>
                  {chaveValida && (
                    <>
                      <div style={{ fontWeight: 700, marginTop: 4 }}>CHAVE DE ACESSO</div>
                      <div style={{ wordBreak: 'break-all' }}>{formatChave(chaveNfce)}</div>
                    </>
                  )}
                  {qrLink ? (
                    <>
                      <div style={{ textAlign: 'center', margin: '6px 0' }}>
                        <img
                          alt="QR Code NFC-e"
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(qrLink)}`}
                          style={{ width: 120, height: 120 }}
                        />
                      </div>
                      <div style={{ wordBreak: 'break-all', fontSize: 10 }}>{qrLink}</div>
                    </>
                  ) : (
                    <div style={{ border: '1px dashed #94a3b8', padding: 12, textAlign: 'center', color: '#94a3b8', margin: '6px 0' }}>QR Code indisponível</div>
                  )}
                </div>
              ) : <div style={{ textAlign: 'center', color: '#b91c1c', fontSize: 11 }}>Cupom sem NFC-e autorizada válida vinculada.</div>}
              <hr />
              <div style={{ textAlign: 'center', fontWeight: 700, fontSize: 10 }}>** 2ª VIA — NÃO É VÁLIDO COMO CUPOM FISCAL **</div>
            </div>

            <button onClick={imprimir}
              style={{ width: '100%', padding: 12, marginTop: 12, border: 'none', borderRadius: 8, background: '#16a34a', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
              <i className="fas fa-print" /> Imprimir 2ª via
            </button>
          </>
        )}

        {erro && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '8px 12px', borderRadius: 6, marginTop: 12, fontSize: 13 }}><i className="fas fa-triangle-exclamation" /> {erro}</div>}
      </div>
    </DraggableWindow>
  );
};

export default WindowReimprimirCupom;
